import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useFrame } from '../contexts/FrameContext';
import { PropertySearch } from '../types';
import CssLanding from './CssLanding';
import { Loader2 } from 'lucide-react';
import { propertyTokenGenerator } from '../services/tokenGenerator';
import { campaignTracking } from '../services/campaignTracking';
import {
  storeUserId,
  setUserIdTracking,
  trackUserMilestone,
  trackEventWithUserId
} from '../utils/analytics';

const UserLandingPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const {
    setPropertySearchId,
    setAddress,
    setAddressLine1,
    setCity,
    setState,
    setZipCode,
    setImageSource,
    setPropertyImage,
    setStyleImages,
    setIsOrientaAddress,
    setIsChapelHouse
  } = useFrame();

  useEffect(() => {
    const loadUserData = async () => {
      if (!userId) {
        setError('No user ID provided');
        setIsLoading(false);
        return;
      }

      // Validate user ID format (alphanumeric, 4-12 characters)
      if (!/^[A-Za-z0-9]{4,12}$/.test(userId)) {
        console.log('Invalid user ID format, treating as regular token');
        navigate(`/${userId}`, { replace: true });
        return;
      }

      try {
        console.log('🎯 UserLandingPage: Loading data for user ID:', userId);
        
        // Set up user tracking
        storeUserId(userId.toUpperCase());
        setUserIdTracking(userId.toUpperCase());
        
        // Track user landing page access with /h route
        trackUserMilestone('landing_page_access_h_route', {
          route_type: 'custom_landing',
          user_id: userId.toUpperCase()
        });

        // Use the same bridge function as PropertyTokenPage to search both tables
        console.log('🔍 Using bridge function to search for property data...');
        const propertyResult = await propertyTokenGenerator.bridgeAccessCodeToPropertySearch(userId.toUpperCase());

        if (!propertyResult) {
          console.log('No property data found for user ID:', userId);
          // Redirect to regular landing page with user tracking
          trackEventWithUserId('user_data_not_found', {
            attempted_user_id: userId,
            fallback_action: 'redirect_to_landing'
          });
          navigate('/', { replace: true });
          return;
        }

        const propertyData = propertyResult.property;
        const fromBridge = propertyResult.fromBridge;
        const accessCode = propertyResult.accessCode;

        console.log('✅ Found property data:', {
          propertyId: propertyData.id,
          fromBridge,
          accessCode,
          address: propertyData.address
        });

        // 🎯 TRACK LANDING PAGE ACCESS FOR CAMPAIGN FUNNEL
        try {
          const campaignInfo = accessCode ? await campaignTracking.getCampaignPropertyInfo(accessCode) : null;
          
          await campaignTracking.trackLandingPageAccessed({
            propertyId: propertyData.id,
            campaignId: campaignInfo?.campaignId,
            accessCode: accessCode || userId.toUpperCase()
          });
          
          console.log('✅ Tracked landing page access for /h route:', {
            propertyId: propertyData.id,
            campaignId: campaignInfo?.campaignId,
            accessCode: accessCode || userId.toUpperCase()
          });
        } catch (trackingError) {
          console.warn('⚠️ Failed to track landing page access for /h route:', trackingError);
        }

        // Check if all 3 main styles are available
        const hasWatercolor = !!propertyData.watercolor_image_url;
        const hasPencil = !!propertyData.pencil_image_url;
        const hasOil = !!propertyData.oil_image_url;
        const hasAllThreeStyles = hasWatercolor && hasPencil && hasOil;

        console.log('Style availability check:', {
          hasWatercolor,
          hasPencil,
          hasOil,
          hasAllThreeStyles
        });

        if (!hasAllThreeStyles) {
          console.log('⚠️ Not all 3 styles available, redirecting to configurator');
          
          // Set up context with available data
          setPropertySearchId(propertyData.id);
          setAddress(propertyData.address);
          // Set structured address fields for dynamic title
          setAddressLine1(propertyData.address_line_1 || null);
          setCity(propertyData.city || null);
          setState(propertyData.state || null);
          setZipCode(propertyData.zip_code || null);
          setImageSource('address'); // Most likely source
          
          // Set available images
          if (hasWatercolor) {
            setPropertyImage(propertyData.watercolor_image_url);
          }
          
          setStyleImages({
            pencil: propertyData.pencil_image_url || null,
            oil: propertyData.oil_image_url || null,
            charcoal: propertyData.charcoal_image_url || null,
            chalk: propertyData.chalk_image_url || null
          });

          // Track partial data and redirect
          trackUserMilestone('partial_images_redirect_to_configurator', {
            available_styles: [
              hasWatercolor && 'watercolor',
              hasPencil && 'pencil', 
              hasOil && 'oil'
            ].filter(Boolean).join(','),
            redirect_reason: 'incomplete_style_set',
            from_bridge: fromBridge,
            access_code: accessCode
          });

          // Navigate using pathname object to ensure absolute path
          // Add query parameter to indicate this is from /h redirect (prevent double tracking)
          navigate({ 
            pathname: '/configure',
            search: '?from=landing_h_redirect'
          }, { replace: true });
          return;
        }

        // All 3 styles available! Set up context for landing page
        console.log('🎨 All 3 styles available, showing custom landing page');
        
        setPropertySearchId(propertyData.id);
        setAddress(propertyData.address);
        // Set structured address fields for dynamic title
        setAddressLine1(propertyData.address_line_1 || null);
        setCity(propertyData.city || null);
        setState(propertyData.state || null);
        setZipCode(propertyData.zip_code || null);
        setImageSource(propertyData.image_source || 'address');
        
        // Set the primary watercolor image
        setPropertyImage(propertyData.watercolor_image_url);
        
        // Set all style images
        setStyleImages({
          pencil: propertyData.pencil_image_url,
          oil: propertyData.oil_image_url,
          charcoal: propertyData.charcoal_image_url || null,
          chalk: propertyData.chalk_image_url || null
        });

        // Check for special addresses
        const addressLower = propertyData.address.toLowerCase();
        if (addressLower.includes('2 chapel hill') && 
            addressLower.includes('sherman') && 
            addressLower.includes('ct')) {
          setIsOrientaAddress(false);
          setIsChapelHouse(true);
        } else {
          setIsOrientaAddress(false);
          setIsChapelHouse(false);
        }

        // Track successful custom landing page load
        trackUserMilestone('custom_landing_page_loaded', {
          property_id: propertyData.id,
          address: propertyData.address,
          all_styles_available: true,
          image_source: propertyData.image_source || 'address',
          from_bridge: fromBridge,
          access_code: accessCode
        });

        trackEventWithUserId('custom_landing_page_view', {
          property_id: propertyData.id,
          route: 'user_h_route',
          content_group1: 'custom_landing',
          from_bridge: fromBridge
        });

        setIsLoading(false);

      } catch (error) {
        console.error('Error loading user data:', error);
        setError(error instanceof Error ? error.message : 'Failed to load user data');
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [userId, navigate, setPropertySearchId, setAddress, setPropertyImage, setStyleImages, setImageSource, setIsOrientaAddress, setIsChapelHouse]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5ECE1] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-600" />
          <p className="text-gray-600">Loading your custom artwork...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#F5ECE1] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">!</span>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Render the landing page with custom images
  return <CssLanding />;
};

export default UserLandingPage; 